import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { useAppStore } from '../../stores';
import { DataCrafterForm, OutputPreview } from '../../components';
import './OutputPreviewContainer.less';
import '../../../custom_shared/styles/common.less';
import { Card, Spin, Tooltip } from 'antd';
import { SelectOutlined } from '@ant-design/icons';

const LOADING_MESSAGES = {
    INITIAL: 'Generating output file…',
    AFTER_60_SECONDS: 'Still generating the output. Large files and templates may require more time.',
    AFTER_120_SECONDS: 'Processing is ongoing. This step may take several minutes.'
} as const;

type Props = {
    hidePopoutButton?: boolean;
    documentPreviewStyle?: React.CSSProperties;
};

const OutputPreviewContainer: React.FC<Props> = ({ hidePopoutButton = false, documentPreviewStyle }) => {
    const { sessionStore } = useAppStore();

    const [activeTab, setActiveTab] = React.useState<string>(
        sessionStore.childSessions && sessionStore.childSessions.length > 0 ? 'child-sessions' : 'file-preview'
    );

    const [loadingMessage, setLoadingMessage] = React.useState<string>(LOADING_MESSAGES.INITIAL);
    const [loadingStartTime, setLoadingStartTime] = React.useState<number | null>(null);

    const handleLoadComplete = React.useCallback(() => {
        sessionStore.setIsLoadingTemplate(false);
    }, [sessionStore]);

    React.useEffect(() => {
        const currentlyLoading = sessionStore.response?.isProcessing || sessionStore.isLoadingTemplate;

        if (currentlyLoading && loadingStartTime === null) {
            setLoadingStartTime(Date.now());
            setLoadingMessage(LOADING_MESSAGES.INITIAL);
        } else if (!currentlyLoading && loadingStartTime !== null) {
            setLoadingStartTime(null);
            setLoadingMessage(LOADING_MESSAGES.INITIAL);
        }
    }, [
        sessionStore.isLoading,
        sessionStore.inProgress,
        sessionStore.response?.isProcessing,
        sessionStore.isLoadingTemplate,
        loadingStartTime
    ]);

    React.useEffect(() => {
        if (loadingStartTime === null) {
            return;
        }

        const timer60s = setTimeout(() => {
            setLoadingMessage(LOADING_MESSAGES.AFTER_60_SECONDS);
        }, 60000);

        const timer120s = setTimeout(() => {
            setLoadingMessage(LOADING_MESSAGES.AFTER_120_SECONDS);
        }, 120000);
        return () => {
            clearTimeout(timer60s);
            clearTimeout(timer120s);
        };
    }, [loadingStartTime]);

    const detachWindow = React.useCallback(
        (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
            event.stopPropagation();
            if (sessionStore.sessionId) {
                localStorage.setItem('dataCrafterSessionTab', sessionStore.getTabJson());
                sessionStore.setDetachedWindow(true);
                const newWindow = window.open(
                    `/data-crafter/preview/${sessionStore.sessionId}`,
                    '_blank',
                    'location=yes,height=700,width=1100,scrollbars=yes,status=yes'
                );
                if (newWindow) {
                    // Interval is needed due to redirections happening in a new window and onbeforeunload is triggered immediately.
                    const timer = setInterval(function () {
                        if (newWindow.closed) {
                            sessionStore.setDetachedWindow(false);
                            clearInterval(timer);
                        }
                    }, 500);
                }
            }
        },
        [sessionStore]
    );

    const popoutButton = React.useMemo(() => {
        if (hidePopoutButton) {
            return null;
        }
        return (
            <Tooltip title="Open in full screen">
                <SelectOutlined className="popout-button" onClick={detachWindow} />
            </Tooltip>
        );
    }, [detachWindow, hidePopoutButton]);

    const isLoading =
        sessionStore.isLoading ||
        sessionStore.inProgress ||
        sessionStore.response?.isProcessing ||
        sessionStore.isLoadingTemplate;

    return (
        <Card
            className={`data-crafter-card ${sessionStore.detachedWindow ? 'collapsed' : ''}`}
            title="Output Preview"
            extra={popoutButton}
        >
            {isLoading && (
                <div className="loading-container">
                    <Spin tip={loadingMessage} />
                </div>
            )}
            <div style={{ visibility: isLoading ? 'hidden' : 'visible' }}>
                <DataCrafterForm />
                <OutputPreview
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    style={documentPreviewStyle}
                    onLoadComplete={handleLoadComplete}
                />
            </div>
        </Card>
    );
};

export default observer(OutputPreviewContainer);
